/**
 * Head pose and the data-normalisation geometry, ported from the Python
 * reference in eye-tracking-sandbox (src/gaze/vision/{camera,head_pose,
 * normalization}.py).
 *
 * The CNN was trained on faces warped into a *virtual camera*: a fixed focal
 * length at a fixed distance, with roll removed. Every constant below is part
 * of that contract. Change one and the crop stops matching what the network was
 * trained on — which does not raise an error, it just quietly returns worse
 * angles. That is why the numbers are here as named constants with the source
 * file they came from, and why linalg.ts reimplements Rodrigues rather than
 * approximating it.
 *
 * Verified against golden vectors emitted from the reference implementation;
 * see linalg.test.ts / headGeometry.test.ts.
 */
import {
  absoluteOrientation,
  column3,
  cross,
  identity3,
  inverse3,
  matMul3,
  matVec3,
  norm3,
  normalize3,
  rodrigues,
  rotationToRvec,
  solveLinearSystem,
  scale3,
  sub3,
  type Mat3,
  type Vec3,
} from './linalg'

/**
 * The six MediaPipe landmarks the pose is solved from: eye corners and mouth
 * corners. Order matters — the 2D and 3D sets are matched positionally and a
 * permutation converges on a plausible but wrong rotation with no error raised.
 * (head_pose.py:6)
 */
export const FACE_LANDMARK_IDS = [33, 133, 362, 263, 61, 291] as const

/**
 * Those landmarks on MediaPipe's canonical face model, in MILLIMETRES.
 *
 * The .obj ships in centimetres and head_pose.py:15 multiplies by 10. Feeding
 * centimetres makes the virtual-camera scale factor wrong by 10× and the crop
 * zooms to nonsense, so the units are baked in here rather than left to a
 * caller. Frame: +x toward the subject's left, +y up, +z out of the face.
 */
export const FACE_MODEL_MM: Vec3[] = [
  [-44.45859, 26.63991, 31.73422],
  [-18.56432, 25.85245, 37.57904],
  [18.56432, 25.85245, 37.57904],
  [44.45859, 26.63991, 31.73422],
  [-24.56206, -43.42621, 42.83884],
  [24.56206, -43.42621, 42.83884],
]

/** Virtual-camera focal length in pixels (cnn.py:16, prepare_dataset.py:38). */
export const FOCAL_NORM = 1920
/** Virtual-camera distance to the face, mm (cnn.py:17, prepare_dataset.py:39). */
export const DISTANCE_NORM = 600
/** Side of the warped crop before the resize to 224 (cnn.py:18). */
export const NORM_CROP = 448
/** Assumed HORIZONTAL field of view — the webcam is not calibrated (E003 config). */
export const DEFAULT_FOV_DEG = 65

/**
 * Camera intrinsics guessed from a field of view (camera.py:4-7).
 *
 * fov_deg is HORIZONTAL: it divides width/2. fy is forced equal to fx and the
 * principal point to the exact image centre. This is deliberately the same
 * fiction the training pipeline used; substituting a real calibration here
 * would make inference inconsistent with training, not more accurate.
 */
export function approxCameraMatrix(width: number, height: number, fovDeg = DEFAULT_FOV_DEG): Mat3 {
  const focal = width / 2 / Math.tan((fovDeg * Math.PI) / 180 / 2)
  return [focal, 0, width / 2, 0, focal, height / 2, 0, 0, 1]
}

export interface HeadPose {
  /** Model → camera rotation. */
  R: Mat3
  /** The same rotation as a Rodrigues vector, for parity with the Python reference. */
  rvec: Vec3
  /** Translation, mm, camera frame. */
  t: Vec3
  /**
   * Mean of the six transformed model points, mm, camera frame.
   * NOT tvec — head_pose.py:30-32 returns the centroid, and using tvec instead
   * aims the virtual camera off the face.
   */
  faceCenter: Vec3
  /** Mean reprojection error, px — the quality signal for rejecting bad solves. */
  reprojectionPx: number
}

function reprojectionError(points2d: Vec3[], K: Mat3, R: Mat3, t: Vec3): number {
  let acc = 0
  for (let i = 0; i < FACE_MODEL_MM.length; i++) {
    const cam = [
      R[0] * FACE_MODEL_MM[i][0] + R[1] * FACE_MODEL_MM[i][1] + R[2] * FACE_MODEL_MM[i][2] + t[0],
      R[3] * FACE_MODEL_MM[i][0] + R[4] * FACE_MODEL_MM[i][1] + R[5] * FACE_MODEL_MM[i][2] + t[1],
      R[6] * FACE_MODEL_MM[i][0] + R[7] * FACE_MODEL_MM[i][1] + R[8] * FACE_MODEL_MM[i][2] + t[2],
    ] as Vec3
    if (Math.abs(cam[2]) < 1e-9) return Number.POSITIVE_INFINITY
    const u = (K[0] * cam[0] + K[2] * cam[2]) / cam[2]
    const v = (K[4] * cam[1] + K[5] * cam[2]) / cam[2]
    acc += Math.hypot(u - points2d[i][0], v - points2d[i][1])
  }
  return acc / FACE_MODEL_MM.length
}

/** Translation that minimises object-space error for a fixed rotation. */
function optimalTranslation(rays: Vec3[], vv: Mat3[], R: Mat3): Vec3 | null {
  // A = Σ(I − Vᵢ); the sum of (Vᵢ − I)·R·pᵢ is the right-hand side.
  const a = new Array<number>(9).fill(0)
  const rhs: Vec3 = [0, 0, 0]
  for (let i = 0; i < rays.length; i++) {
    const p = FACE_MODEL_MM[i]
    const rp = matVec3(R, p)
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const imv = (r === c ? 1 : 0) - vv[i][r * 3 + c]
        a[r * 3 + c] += imv
        rhs[r] -= imv * rp[c]
      }
    }
  }
  const inv = inverse3(a)
  return inv ? matVec3(inv, rhs) : null
}

/**
 * Pose from 2D–3D correspondences, standing in for cv2.solvePnP(SOLVEPNP_SQPNP).
 *
 * Uses Lu–Hager–Mjolsness orthogonal iteration: alternate the closed-form
 * optimal translation with an absolute-orientation solve for the rotation,
 * minimising object-space collinearity error. SQPNP itself is not reproduced —
 * it is a very different algorithm — but both look for the global minimum of
 * the same geometry, so on well-posed input they agree.
 *
 * The six face points are nearly coplanar, which is exactly where a single-start
 * iteration can settle into the mirror-image local minimum. Hence the restart
 * grid: candidates are scored by REPROJECTION error and the best is kept.
 */
export function solveHeadPose(
  points2d: Array<[number, number]>,
  K: Mat3,
): HeadPose | null {
  if (points2d.length !== FACE_MODEL_MM.length) return null
  for (const p of points2d) {
    if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) return null
  }
  const Kinv = inverse3(K)
  if (!Kinv) return null

  const asVec3 = points2d.map((p) => [p[0], p[1]] as unknown as Vec3)

  // Line-of-sight rays and their projection matrices Vᵢ = vvᵀ/(vᵀv).
  const rays: Vec3[] = []
  const vv: Mat3[] = []
  for (const p of points2d) {
    const ray = matVec3(Kinv, [p[0], p[1], 1])
    const d = ray[0] * ray[0] + ray[1] * ray[1] + ray[2] * ray[2]
    if (d < 1e-18) return null
    rays.push(ray)
    vv.push([
      (ray[0] * ray[0]) / d,
      (ray[0] * ray[1]) / d,
      (ray[0] * ray[2]) / d,
      (ray[1] * ray[0]) / d,
      (ray[1] * ray[1]) / d,
      (ray[1] * ray[2]) / d,
      (ray[2] * ray[0]) / d,
      (ray[2] * ray[1]) / d,
      (ray[2] * ray[2]) / d,
    ])
  }

  const centroid: Vec3 = [0, 0, 0]
  for (const p of FACE_MODEL_MM) {
    centroid[0] += p[0] / FACE_MODEL_MM.length
    centroid[1] += p[1] / FACE_MODEL_MM.length
    centroid[2] += p[2] / FACE_MODEL_MM.length
  }
  const centred = FACE_MODEL_MM.map((p) => sub3(p, centroid))

  let best: HeadPose | null = null

  for (const start of startingRotations()) {
    let R = start
    let t: Vec3 | null = optimalTranslation(rays, vv, R)
    if (!t) continue

    for (let iter = 0; iter < 60; iter++) {
      // qᵢ = Vᵢ(R·pᵢ + t): each observation pulled onto its line of sight.
      const q: Vec3[] = []
      const qMean: Vec3 = [0, 0, 0]
      for (let i = 0; i < rays.length; i++) {
        const cam = matVec3(R, FACE_MODEL_MM[i])
        const pt: Vec3 = [cam[0] + t[0], cam[1] + t[1], cam[2] + t[2]]
        const proj = matVec3(vv[i], pt)
        q.push(proj)
        qMean[0] += proj[0] / rays.length
        qMean[1] += proj[1] / rays.length
        qMean[2] += proj[2] / rays.length
      }
      const nextR = absoluteOrientation(
        centred,
        q.map((p) => sub3(p, qMean)),
      )
      const nextT = optimalTranslation(rays, vv, nextR)
      if (!nextT) break

      let delta = 0
      for (let i = 0; i < 9; i++) delta += Math.abs(nextR[i] - R[i])
      R = nextR
      t = nextT
      if (delta < 1e-12) break
    }

    if (!t || t[2] <= 0) continue // the face must be in front of the camera

    // Orthogonal iteration minimises object-space error, but the reference
    // solver minimises something closer to image-plane residuals. On landmarks
    // that no single pose explains exactly — i.e. every real frame — those two
    // optima are not the same point, so finish on the reprojection objective.
    const refined = refinePose(points2d, K, R, t)
    R = refined.R
    t = refined.t

    const err = reprojectionError(asVec3, K, R, t)
    if (!Number.isFinite(err)) continue
    if (!best || err < best.reprojectionPx) {
      const faceCenter: Vec3 = [0, 0, 0]
      for (const p of FACE_MODEL_MM) {
        const cam = matVec3(R, p)
        faceCenter[0] += (cam[0] + t[0]) / FACE_MODEL_MM.length
        faceCenter[1] += (cam[1] + t[1]) / FACE_MODEL_MM.length
        faceCenter[2] += (cam[2] + t[2]) / FACE_MODEL_MM.length
      }
      best = { R, rvec: rotationToRvec(R), t, faceCenter, reprojectionPx: err }
    }
  }

  return best
}

/**
 * Levenberg–Marquardt refinement of a pose against image-plane residuals.
 *
 * Six parameters (Rodrigues vector then translation), twelve residuals (u and v
 * per landmark). The Jacobian is central-difference rather than analytic: at
 * 6×12 the cost is negligible next to everything else in the frame, and a
 * hand-derived Jacobian is a rich source of sign errors that would show up only
 * as slightly-wrong poses.
 */
function refinePose(
  points2d: Array<[number, number]>,
  K: Mat3,
  R0: Mat3,
  t0: Vec3,
): { R: Mat3; t: Vec3 } {
  const residuals = (p: number[]): number[] => {
    const R = rodrigues([p[0], p[1], p[2]])
    const out: number[] = []
    for (let i = 0; i < FACE_MODEL_MM.length; i++) {
      const m = FACE_MODEL_MM[i]
      const z = R[6] * m[0] + R[7] * m[1] + R[8] * m[2] + p[5]
      if (Math.abs(z) < 1e-9) return out.length ? out.map(() => 1e6) : [1e6]
      const x = R[0] * m[0] + R[1] * m[1] + R[2] * m[2] + p[3]
      const y = R[3] * m[0] + R[4] * m[1] + R[5] * m[2] + p[4]
      out.push((K[0] * x + K[2] * z) / z - points2d[i][0])
      out.push((K[4] * y + K[5] * z) / z - points2d[i][1])
    }
    return out
  }
  const cost = (r: number[]) => r.reduce((a, v) => a + v * v, 0)

  const rv = rotationToRvec(R0)
  let params = [rv[0], rv[1], rv[2], t0[0], t0[1], t0[2]]
  let r = residuals(params)
  let best = cost(r)
  let lambda = 1e-3

  for (let iter = 0; iter < 40; iter++) {
    const n = r.length
    const j: number[][] = Array.from({ length: n }, () => new Array<number>(6).fill(0))
    for (let k = 0; k < 6; k++) {
      const step = Math.max(1e-7, Math.abs(params[k]) * 1e-7)
      const plus = params.slice()
      const minus = params.slice()
      plus[k] += step
      minus[k] -= step
      const rp = residuals(plus)
      const rm = residuals(minus)
      if (rp.length !== n || rm.length !== n) continue
      for (let i = 0; i < n; i++) j[i][k] = (rp[i] - rm[i]) / (2 * step)
    }

    const jtj = Array.from({ length: 6 }, () => new Array<number>(6).fill(0))
    const jtr = new Array<number>(6).fill(0)
    for (let i = 0; i < n; i++) {
      for (let a = 0; a < 6; a++) {
        jtr[a] += j[i][a] * r[i]
        for (let b = a; b < 6; b++) jtj[a][b] += j[i][a] * j[i][b]
      }
    }
    for (let a = 0; a < 6; a++) for (let b = 0; b < a; b++) jtj[a][b] = jtj[b][a]

    let improved = false
    for (let attempt = 0; attempt < 6; attempt++) {
      const damped = jtj.map((row, i) => row.map((v, k) => (i === k ? v * (1 + lambda) : v)))
      const delta = solveLinearSystem(damped, jtr.map((v) => -v))
      if (!delta) {
        lambda *= 10
        continue
      }
      const candidate = params.map((v, i) => v + delta[i])
      const rc = residuals(candidate)
      const cc = cost(rc)
      if (rc.length === n && cc < best) {
        params = candidate
        r = rc
        best = cc
        lambda = Math.max(lambda / 10, 1e-12)
        improved = true
        break
      }
      lambda *= 10
    }
    if (!improved) break
  }

  return {
    R: rodrigues([params[0], params[1], params[2]]),
    t: [params[3], params[4], params[5]],
  }
}

/**
 * Restart grid for the pose iteration, cached because it never changes.
 *
 * Coarse on purpose: it only has to land in the basin of the true minimum, and
 * the iteration does the rest. Six points × 60 iterations × this many starts is
 * still microseconds, which is nothing next to the ResNet forward pass that
 * follows it.
 */
let STARTS: Mat3[] | null = null
function startingRotations(): Mat3[] {
  if (STARTS) return STARTS
  const out: Mat3[] = [identity3()]
  const yaws = [-50, -25, 25, 50]
  const pitches = [-30, -15, 15, 30]
  for (const yaw of yaws) {
    out.push(rodrigues([0, (yaw * Math.PI) / 180, 0]))
  }
  for (const pitch of pitches) {
    out.push(rodrigues([(pitch * Math.PI) / 180, 0, 0]))
  }
  for (const yaw of [-35, 35]) {
    for (const pitch of [-20, 20]) {
      out.push(matMul3(rodrigues([(pitch * Math.PI) / 180, 0, 0]), rodrigues([0, (yaw * Math.PI) / 180, 0])))
    }
  }
  // A half-turn about x: the near-coplanar mirror solution the reference solver
  // sometimes settles on, which a purely frontal restart set would never reach.
  out.push(rodrigues([Math.PI, 0, 0]))
  out.push(rodrigues([-2.5, 0, 0]))
  out.push(rodrigues([2.5, 0, 0]))
  STARTS = out
  return out
}

export interface Normalization {
  /** Forward source→destination homography for the 448² crop. */
  W: Mat3
  /** Camera → virtual-camera rotation, rows [right; down; forward]. */
  Rnorm: Mat3
  /** Third column of Rnorm·R_head: the face's +z axis in virtual-camera coords. */
  headAxis: Vec3
  distanceMm: number
}

/**
 * The virtual-camera homography (normalization.py:5-27).
 *
 * W = C_norm · S · R_norm · K⁻¹, a FORWARD map. cv2.warpPerspective inverts it
 * internally; a WebGL warp that samples the source per destination pixel needs
 * W⁻¹ instead, so whoever consumes this must be explicit about direction.
 *
 * Roll is removed (`down` is built from the head's own x-axis) but yaw and
 * pitch relative to the virtual camera are deliberately preserved — the network
 * is supposed to see them.
 */
export function normalizationMatrices(
  K: Mat3,
  headR: Mat3,
  faceCenter: Vec3,
  focalNorm = FOCAL_NORM,
  distanceNorm = DISTANCE_NORM,
  outSize = NORM_CROP,
): Normalization | null {
  const distance = norm3(faceCenter)
  if (!Number.isFinite(distance) || distance < 1e-6) return null

  const forward = scale3(faceCenter, 1 / distance)
  const down = normalize3(cross(forward, column3(headR, 0)))
  if (!down) return null
  const right = normalize3(cross(down, forward))
  if (!right) return null

  // Rows, not columns: the convention is v_norm = R_norm · v_cam.
  const Rnorm: Mat3 = [
    right[0],
    right[1],
    right[2],
    down[0],
    down[1],
    down[2],
    forward[0],
    forward[1],
    forward[2],
  ]

  const z = distanceNorm / distance
  const half = outSize / 2
  const Cnorm: Mat3 = [focalNorm, 0, half, 0, focalNorm, half, 0, 0, 1]
  const S: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, z]

  const Kinv = inverse3(K)
  if (!Kinv) return null
  const W = matMul3(matMul3(matMul3(Cnorm, S), Rnorm), Kinv)

  return { W, Rnorm, headAxis: column3(matMul3(Rnorm, headR), 2), distanceMm: distance }
}

/** Gaze unit vector → (pitch, yaw) radians, matching eval/metrics.py:75-80. */
export function vectorToAngles(g: Vec3): [number, number] {
  const pitch = Math.asin(Math.min(1, Math.max(-1, -g[1])))
  const yaw = Math.atan2(-g[0], -g[2])
  return [pitch, yaw]
}

/** (pitch, yaw) radians → gaze unit vector, matching eval/metrics.py:64-72. */
export function anglesToVector(pitch: number, yaw: number): Vec3 {
  return [-Math.cos(pitch) * Math.sin(yaw), -Math.sin(pitch), -Math.cos(pitch) * Math.cos(yaw)]
}

export { rodrigues }
